import type { Building } from "building-types/building/buildingTypes";
import type {
  AbsoluteVerticalDatumData,
  RelativeDatumTable,
  RelativeVerticalDatumData,
} from "building-types/columns/columnDatumTypes";
import { ColumnSubType } from "building-types/columns/columnTypesEnum";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import { VerticalDatumType } from "building-types/datums/datumsTypesEnum";
import type { FoundationPatternEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { StairBlockHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { MaterialSrcOverride } from "building-types/materials/materialTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import {
  defaultConstruction,
  STAIR_DEFAULT_MATERIAL_OVERRIDE,
  StairConstructionType,
} from "building-types/stairs/stairConstruction";
import type {
  GeneratedStairBlock,
  GeneratedStairSystem,
  StairBlock,
  StairHosts,
  StairRun,
  StairSurfacePoly,
  StairSystem,
} from "building-types/stairs/stairTypes";
import { EdgeType, StairBlockType, StairPlanes } from "building-types/stairs/stairTypesEnum";
import type { RWTable, Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNullOrUndefined, assertIsNumber } from "common/utils/assertOfType";
import { levelInchesNew, levelInchesToWorld, worldInchesNew } from "common/utils/nominalNumbers";
import type { Mutable } from "common/utils/types";
import { defaultStairConfig } from "doc/elements/stairSystem/stairConstants";
import { docEntityTableFilter } from "doc/entity/docEntityTableFilter";
import { getRelativeDatumHeight } from "doc/utils/datumsUtils";
import { aabb2Overlaps } from "geom/aabb2";
import type { Face } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrudePolyLine2AlongNormal } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { line2IsCoincident } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planeByThreePoints, planeXYAtZ } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonPlanarGraphNewDebug } from "geom/polygonPlanarGraph";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2IsRectangle,
  polyLine2Rotate,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween, vec2Rotate, vec2Scale, vec2Sub } from "geom/vec2";
import { getPlaneZ } from "../datums";
import { resolvePostCentroid } from "../foundation/foundationPattern";
import type { StairSolverContext } from "./stairBlock";
import { getInitialStairBlockTreadsAndRunHeights, resolveStairSystemHosting } from "./stairBlock";
import { stairBlockNodeTableNew } from "./stairBlockNodeTable";
import { stairBlockRiserTableNew } from "./stairBlockTraversal";
import { isValidBlockRectangle } from "./stairBlockValidate";
import { addStairLandingPlanOutlineAndExtrusion } from "./stairLanding";
import { addStairRunExtrusions, makeStairRunGeom, makeStairRunTopSurfaces } from "./stairRun";
import { resolveRisers, spatialBlockMirror, unionStairTreadsAndLandingsForPDFPlanView } from "./stairUtil";

interface GenerateStairSystemOptions {
  readonly levels: Table<Level>;
  readonly stairSystem: StairSystem;
  readonly stairBlocks: StairBlock[];
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
  readonly rooms: Table<Room>;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly stairSystemOptionChoices: OptionChoices;
  readonly stairBlocksOptionChoices: OptionChoices;
  readonly resolvedDatums: ResolvedDatumTable;
}

export function generateStairSystem({
  stairSystem,
  stairBlocks,
  levels,
  mirror,
  mirrorTranslationX,
  rooms,
  roomFloorAssemblies,
  stairBlocksOptionChoices,
  resolvedDatums,
}: GenerateStairSystemOptions): { generated: GeneratedStairSystem; issues: BuildingGenerationIssue[] } {
  const issues: BuildingGenerationIssue[] = [];

  const { levelId, config: customConfig, id, contextAware } = stairSystem;
  const pickHandle: DocEntityHandle = { type: "STAIR_SYSTEM", id: id };
  const config = customConfig || defaultStairConfig;
  const { desiredRiserHeight, maxRiserHeight, desiredTreadDepth, minTreadDepth, maxTreadDepth, landingMaterial } =
    config;
  let { landingDepth } = config;
  if (landingDepth === undefined) {
    landingDepth = desiredRiserHeight;
  }
  const { floorElevation, topOfWall, floorPlateThickness } = levels[levelId];

  const bottomOfStair = floorElevation;
  const stairHeight = levelInchesNew(topOfWall + floorPlateThickness);

  const { riserCount: totalRiserCount, riserHeightActual } = resolveRisers(
    stairHeight,
    desiredRiserHeight,
    maxRiserHeight,
  );

  const hosts: StairHosts = {
    ...stairBlocks.reduce((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {}),
    ...rooms,
  };

  if (mirror) {
    stairBlocks = stairBlocks.map((block) => {
      const { edgeIds, outline } = spatialBlockMirror(block, mirror, mirrorTranslationX);
      return {
        ...block,
        edgeIds,
        outline,
      };
    });
  }

  const ctx: StairSolverContext = {
    building: {
      rooms,
      levels,
      resolvedDatums,
      mirror,
      optionChoices: stairBlocksOptionChoices,
      roomFloorAssemblies,
    },
    stairSystem,
    issues,
    stairBlocks,
    hosts,
  };

  stairBlocks = resolveStairSystemHosting({
    ...ctx,
    stairBlocks,
  });

  const [graph] = polygonPlanarGraphNewDebug(
    stairBlocks.map((x) => x.outline),
    true,
  );

  const stairBlocksResolved = getInitialStairBlockTreadsAndRunHeights(
    stairBlocks,
    desiredTreadDepth,
    minTreadDepth,
    maxTreadDepth,
  );

  const stairBlockNodeTable = stairBlockNodeTableNew(
    stairBlocksResolved,
    graph,
    resolvedDatums,
    levels,
    totalRiserCount,
    riserHeightActual,
    stairHeight,
    levelId,
  );

  const blockRiserTable = stairBlockRiserTableNew(stairBlockNodeTable, riserHeightActual);

  let currentRiserCount = 0;

  const resultBlocks: Mutable<GeneratedStairSystem["blocks"]> = {};

  for (const blockIndex of Object.keys(stairBlocksResolved)) {
    const block = stairBlocksResolved[blockIndex];
    const previousBlockType = stairBlocksResolved[parseInt(blockIndex) - 1]?.type;

    const blockRiserInfo = blockRiserTable[blockIndex];

    let z: WorldInches | undefined;
    switch (blockRiserInfo.type) {
      case StairBlockType.RUN:
        if (blockRiserInfo?.bottom !== undefined) {
          z = levelInchesToWorld(blockRiserInfo.bottom, levels[levelId]?.floorElevation);
        }
        break;
      case StairBlockType.LANDING:
        if (blockRiserInfo?.top !== undefined) {
          z = worldInchesNew(levelInchesToWorld(blockRiserInfo.top, levels[levelId]?.floorElevation) - landingDepth);
        }
        break;
    }

    const blockHandle: DocEntityHandle = { type: "STAIR_BLOCK", id: block.id };
    const blockResult: GeneratedStairBlock = {
      ...block,
      geom: new GeomTagGroup<MaterialSrcOverride>(),
      stairPlanPolys: new Array<StairSurfacePoly>(),
      pickHandle: blockHandle,
      autoGenerated: block.generated,
      stairBlockId: block.id,
      startEdgeIndex: block.type === StairBlockType.RUN ? block.startEdgeIndex : -1,
      planes: {},
      wallTrimFaces: [],
    };

    if (z !== undefined) {
      const bottomPlane = planeByThreePoints(0, 0, z, 1, 0, z, 0, 1, z);
      if (bottomPlane) {
        blockResult.planes[StairPlanes.BOTTOM] = bottomPlane;
      }
    }

    resultBlocks[block.id] = blockResult;
    let blockRiserHeight = riserHeightActual;

    switch (block.type) {
      case StairBlockType.RUN: {
        const {
          treadJustification,
          riserCount,
          normal,
          treadDepthActual,
          treadCount,
          outline,
          startEdgeIndex,
          length,
          construction = defaultConstruction,
        } = block;

        if (startEdgeIndex === undefined) {
          continue;
        }

        //// OLD METHOD
        const risersLeftToNextFloor = totalRiserCount - currentRiserCount;
        let risersToPlace = riserCount > risersLeftToNextFloor ? risersLeftToNextFloor : undefined;
        ////

        let bottomOfRun: number | undefined = currentRiserCount * blockRiserHeight;
        if (contextAware) {
          currentRiserCount = blockRiserInfo.bottomRiser;
          bottomOfRun = blockRiserInfo.bottom;
          if (blockRiserInfo.top !== undefined && blockRiserInfo.bottom !== undefined) {
            const { riserCount, riserHeightActual: riserHeightActualLocal } = resolveRisers(
              blockRiserInfo.top - blockRiserInfo.bottom,
              desiredRiserHeight,
              maxRiserHeight,
            );
            risersToPlace = riserCount;
            blockRiserHeight = riserHeightActualLocal;
          }
        }

        let stairRun: StairRun;
        if (isValidBlockRectangle(outline) && construction.type !== StairConstructionType.GENERIC) {
          assertIsNumber(bottomOfRun);

          stairRun = makeStairRunGeom({
            block,
            config,
            outline,
            startEdgeIndex,
            length,
            normal,
            treadDepthActual,
            treadJustification,
            bottomOfRun: bottomOfRun + bottomOfStair,
            riserHeight: blockRiserHeight,
            treadCount,
            stopAtRiser: risersToPlace,
            bottomOfStair,
            construction,
            issues,
          });
        } else {
          if (construction.type !== StairConstructionType.GENERIC) {
            issues.push({
              type: BuildingGenerationIssueType.STAIR_RUN_TYPE_INCOMPATIBLE,
              handle: {
                type: "STAIR_BLOCK",
                id: block.id,
              },
            });
          }

          assertIsNumber(bottomOfRun);

          // a generic stair is the same as the old version
          stairRun = makeStairRunTopSurfaces({
            config,
            outline,
            startEdgeIndex,
            length,
            normal,
            treadDepthActual,
            treadJustification,
            bottomOfRun: bottomOfRun + bottomOfStair,
            riserHeight: blockRiserHeight,
            treadCount,
            stopAtRiser: risersToPlace,
            bottomOfStair,
            construction,
          });
        }

        for (const name in stairRun.planes) {
          resultBlocks[block.id].planes[name] = stairRun.planes[name];
        }

        addStairRunExtrusions(stairRun, blockResult, riserHeightActual);

        if (blockResult.geom.geom.length === 0) {
          issues.push({
            type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
            message: `In current state, stair ${block.name} generates no geometry`,
            handle: {
              type: "STAIR_BLOCK",
              id: block.id,
            },
          });
        }
        currentRiserCount += risersToPlace ?? riserCount;
        break;
      }
      case StairBlockType.LANDING: {
        const { stepUpFromPreviousLanding: stepUpFromAdjacentLanding, outline } = block;

        //// OLD METHOD
        if (
          stepUpFromAdjacentLanding &&
          previousBlockType === StairBlockType.LANDING &&
          currentRiserCount < totalRiserCount
        ) {
          currentRiserCount++;
        }

        const risersLeftToNextFloor = totalRiserCount - currentRiserCount;
        if (risersLeftToNextFloor <= 0) {
          currentRiserCount = currentRiserCount + risersLeftToNextFloor;
        }
        ////

        let landingHeight = currentRiserCount * blockRiserHeight + bottomOfStair;
        if (contextAware) {
          assertIsNotNullOrUndefined(blockRiserInfo?.top);

          currentRiserCount = blockRiserInfo.topRiser;
          landingHeight = blockRiserInfo.top + bottomOfStair;
          blockRiserHeight = riserHeightActual;
        }

        addStairLandingPlanOutlineAndExtrusion(landingHeight, landingDepth, outline, blockResult, landingMaterial);
        break;
      }
    }
  }

  unionStairTreadsAndLandingsForPDFPlanView(stairBlockNodeTable, resultBlocks);

  return {
    generated: {
      ...stairSystem,
      id,
      pickHandle,
      levelId,
      blocks: resultBlocks,
      wallTrimFaces: [], // Aggregated from blocks after instancing
    },
    issues,
  };
}

/**
 * Aggregate the wallTrimFaces from the blocks into the system.
 */
export function aggregateWallTrimFacesFromBlocks(system: GeneratedStairSystem): GeneratedStairSystem {
  const wallTrimFaces: Face[] = [];
  for (const block of Object.values(system.blocks)) {
    wallTrimFaces.push(...block.wallTrimFaces);
  }
  return { ...system, wallTrimFaces };
}

interface AddFoundations {
  resolvedDatums: ResolvedDatumTable;
  columns: ColumnCatalogSettings;
  resultBlocks: RWTable<GeneratedStairBlock>;
  issues: BuildingGenerationIssue[];
  levelId: string;
  levels: Building["levels"];
  foundationPatternEdgeBehaviors: Table<FoundationPatternEdgeBehavior>;
}

export function addPosts({
  resolvedDatums,
  columns,
  resultBlocks,
  issues,
  levelId,
  levels,
  foundationPatternEdgeBehaviors,
}: AddFoundations) {
  if (!columns?.columnTable) {
    return;
  }

  for (const { outline, edgeIds, planes, geom, stairBlockId } of Object.values(resultBlocks)) {
    const handle: StairBlockHandle = { type: "STAIR_BLOCK", id: stairBlockId };

    const queryZ = stairBlockBottomQueryNew({ planes });

    const foundationPatterns = Object.values(
      docEntityTableFilter(foundationPatternEdgeBehaviors, (x) => x.parentId === stairBlockId).newTable,
    );

    for (const { foundationPattern, edgeId, disabled } of foundationPatterns) {
      if (disabled) {
        continue;
      }

      const edge = polyLine2Edge(outline, edgeIds.indexOf(edgeId));

      const angle = vec2AngleBetween(edge[2] - edge[0], edge[3] - edge[1], 0, 1);

      if (foundationPattern.patternType !== FoundationSectionSizeType.ISOLATED) {
        issues.push({
          type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
          message: `Foundation pattern type ${foundationPattern.patternType} is not supported for posts`,
          handle,
        });
        continue;
      }

      if (foundationPattern.hostType !== "STAIR_BLOCK") {
        continue;
      }

      const { positions, postId, verticalDatum } = foundationPattern;

      const post = columns?.columnTable[postId];

      if (!post) {
        issues.push({
          type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
          message: `When adding posts to ${foundationPattern.patternType} foundation pattern, post '${postId}' not found in column catalog`,
          handle,
        });
        continue;
      }

      if (post.subType !== ColumnSubType.STRUCTURAL) {
        issues.push({
          type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
          message: `When adding posts to ${foundationPattern.patternType} foundaton pattern, post '${postId}' is not a structural post`,
          handle,
        });
        continue;
      }

      for (const position of positions) {
        const [x, y] = resolvePostCentroid(edge, foundationPattern, position);

        let datumsTopAndBot: { [id: string]: WorldInches } = {};

        if (verticalDatum.type === VerticalDatumType.ABSOLUTE) {
          datumsTopAndBot = resolveAbsoluteVerticalDatumForTopAndBottom(verticalDatum, resolvedDatums, x, y);
        } else if (verticalDatum.type === VerticalDatumType.RELATIVE) {
          datumsTopAndBot = resolveRelativeVerticalDatumForTopAndBottom(verticalDatum, levelId, levels, queryZ, x, y);
        }

        if (verticalDatum.topDatumId === undefined || datumsTopAndBot[verticalDatum.topDatumId] === undefined) {
          issues.push({
            type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
            message: `When adding posts to ${foundationPattern.patternType} foundation pattern, datum '${verticalDatum.topDatumId}' not found`,
            handle,
          });
          continue;
        }
        if (verticalDatum.bottomDatumId === undefined || datumsTopAndBot[verticalDatum.bottomDatumId] === undefined) {
          issues.push({
            type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
            message: `When adding posts to ${foundationPattern.patternType} foundation pattern, datum '${verticalDatum.bottomDatumId}' not found`,
            handle,
          });
          continue;
        }

        const bottom = datumsTopAndBot[verticalDatum.bottomDatumId] + (verticalDatum.bottomOffset ?? 0);

        const top = datumsTopAndBot[verticalDatum.topDatumId] + (verticalDatum.topOffset ?? 0);
        const height = top - bottom;

        const l = post.postThickness / 2;
        const w = post.postWidth / 2;
        let profile = polyLine2New([x - l, y - w, x - l, y + w, x + l, y + w, x + l, y - w, x - l, y - w]);

        profile = polyLine2Rotate(profile, -angle, x, y);

        if (profile?.length > 0) {
          const bottomPlane = planeXYAtZ(bottom);
          assertIsNotNullOrUndefined(bottomPlane);
          geom.add(extrudePolyLine2AlongNormal(profile, bottomPlane, height), STAIR_DEFAULT_MATERIAL_OVERRIDE);
        }
      }
    }
  }
}

export function stairBlockBottomQueryNew({
  planes,
}: Pick<GeneratedStairBlock, "planes">): (x: number, y: number) => WorldInches {
  const maxCandidates: Plane[] = [];
  if (planes[StairPlanes.BOTTOM]) {
    maxCandidates.push(planes[StairPlanes.BOTTOM]);
  }
  if (planes[StairPlanes.UNDERSIDE]) {
    maxCandidates.push(planes[StairPlanes.UNDERSIDE]);
  }

  return (x: number, y: number) => {
    let highest = Number.MIN_SAFE_INTEGER;
    for (const plane of maxCandidates) {
      const z = getPlaneZ(plane, [x, y]);
      if (z > highest) {
        highest = z;
      }
    }

    if (planes[StairPlanes.LANDING]) {
      const landingZ = getPlaneZ(planes[StairPlanes.LANDING], [x, y]);
      if (landingZ < highest) {
        highest = landingZ;
      }
    }

    return worldInchesNew(highest);
  };
}

export function makeStairUpDownArrows(stairPlanPolys: (StairSurfacePoly | undefined)[]): PolyLine2[] {
  const arrowPolys: PolyLine2[] = [];

  //@TODO - suppressing arrows for now. This needs more work for multi-level stairs etc.
  const suppressArrows = true;

  if (suppressArrows) {
    return arrowPolys;
  }

  let startX: number | undefined = undefined,
    startY: number | undefined = undefined,
    endX: number | undefined = undefined,
    endY: number | undefined = undefined;
  const stairPlanPolysFiltered = stairPlanPolys
    .filter((stairPlanPoly) => stairPlanPoly?.edgeTypes !== undefined)
    .sort((a, b) => (a !== undefined && b !== undefined ? a.height - b.height : 0));
  const lastIndex = stairPlanPolysFiltered.length - 1;
  stairPlanPolysFiltered.forEach((stairPlanPoly, indexStairPlanPoly) => {
    if (!stairPlanPoly) {
      return;
    }
    const { outline, edgeTypes } = stairPlanPoly;
    if (indexStairPlanPoly === 0 || indexStairPlanPoly === lastIndex) {
      for (const indexAsString in edgeTypes) {
        const index = +indexAsString;
        const edgeType = edgeTypes[index];

        const x1 = outline[index * 2];
        const y1 = outline[index * 2 + 1];

        const x2 = outline[index * 2 + 2];
        const y2 = outline[index * 2 + 3];

        if (edgeType === EdgeType.FRONT && indexStairPlanPoly === 0) {
          startX = (x1 + x2) / 2;
          startY = (y1 + y2) / 2;
        }
        if (edgeType === EdgeType.BACK && indexStairPlanPoly === lastIndex) {
          endX = (x1 + x2) / 2;
          endY = (y1 + y2) / 2;
        }
      }
    }
  });

  if (startX !== undefined && startY !== undefined && endX !== undefined && endY !== undefined) {
    const sfArrow = 0.1;
    const [upX, upY] = vec2Sub(endX, endY, startX, startY);
    const [diffX, diffY] = vec2Scale(upX, upY, 0.333);
    const [upXScaled, upYScaled] = vec2Scale(diffX, diffY, sfArrow);
    const [rotPosX, rotPosY] = vec2Rotate(upXScaled, upYScaled, Math.PI / 4);
    const [rotNegX, rotNegY] = vec2Rotate(upXScaled, upYScaled, -Math.PI / 4);

    arrowPolys.push(polyLine2New([startX, startY, startX + diffX, startY + diffY]));
    arrowPolys.push(polyLine2New([startX + diffX, startY + diffY, startX + diffX - rotPosX, startY + diffY - rotPosY]));
    arrowPolys.push(polyLine2New([startX + diffX, startY + diffY, startX + diffX - rotNegX, startY + diffY - rotNegY]));

    arrowPolys.push(polyLine2New([endX, endY, endX - diffX, endY - diffY]));
    arrowPolys.push(polyLine2New([endX - diffX, endY - diffY, endX - diffX + rotPosX, endY - diffY + rotPosY]));
    arrowPolys.push(polyLine2New([endX - diffX, endY - diffY, endX - diffX + rotNegX, endY - diffY + rotNegY]));
  }
  return arrowPolys;
}

export function splitStairPlanOutlinesConsideringNosing(
  outline: PolyLine2,
  nosing?: number,
  edgeTypes?: (EdgeType | undefined)[],
  addFrontToDash?: boolean,
): { solidPolys: PolyLine2[]; dashedPolys: PolyLine2[] } {
  const solidPolys: PolyLine2[] = [];
  const dashedPolys: PolyLine2[] = [];

  if (edgeTypes && nosing && nosing !== 0) {
    for (const indexAsString of Object.keys(edgeTypes)) {
      const index = +indexAsString;
      const edgeType = edgeTypes[index];

      const x1 = outline[index * 2];
      const y1 = outline[index * 2 + 1];

      const x2 = outline[index * 2 + 2];
      const y2 = outline[index * 2 + 3];

      let pl = polyLine2ByTwoPoints(x1, y1, x2, y2);

      if (edgeType === EdgeType.BACK) {
        dashedPolys.push(pl);
        solidPolys.push(offsetPolyLine2(pl, -nosing));
        continue;
      }
      if (edgeType === EdgeType.FRONT) {
        if (addFrontToDash) {
          dashedPolys.push(pl);
        }
        pl = polyLine2New([x1, y1, ...offsetPolyLine2(pl, nosing), x2, y2]);
      }
      solidPolys.push(pl);
    }
  } else {
    solidPolys.push(outline);
  }
  return { solidPolys, dashedPolys };
}

/**
 * Find the shared edge between two closed polylines (adjacent tread outlines).
 * Returns the two endpoints [x0, y0, x1, y1] or undefined if no shared edge is found.
 */
function polyLine2FindSharedEdge(polyA: PolyLine2, polyB: PolyLine2, tolerance: number = 0.5): PolyLine2 | undefined {
  const aabbA = polyLine2AABB2(polyA);
  const aabbB = polyLine2AABB2(polyB);
  if (!aabb2Overlaps(aabbA, aabbB)) {
    return undefined;
  }

  const edgeCountA = polyLine2EdgeCount(polyA);
  const edgeCountB = polyLine2EdgeCount(polyB);

  for (let i = 0; i < edgeCountA; i++) {
    const a = polyLine2Edge(polyA, i);

    for (let j = 0; j < edgeCountB; j++) {
      const b = polyLine2Edge(polyB, j);

      if (line2IsCoincident(a[0], a[1], a[2], a[3], b[0], b[1], b[2], b[3], tolerance)) {
        return a;
      }
    }
  }
  return undefined;
}

const BREAKLINE_ANGLE_RAD = -Math.PI / 6;
const BREAKLINE_ANGLE_COS = Math.cos(BREAKLINE_ANGLE_RAD);
const BREAKLINE_ANGLE_SIN = Math.sin(BREAKLINE_ANGLE_RAD);
const BREAKLINE_ANGLE_TAN = Math.tan(BREAKLINE_ANGLE_RAD);

const SQUIGGLE_SEG1_ANGLE = Math.PI / 4;
const SQUIGGLE_SEG1_LEN = 8 * Math.sqrt(2);

const SQUIGGLE_SEG2_TURN = (-3 * Math.PI) / 4;
const SQUIGGLE_SEG2_LEN = 16;

const SQUIGGLE_SEG3_TURN = -SQUIGGLE_SEG2_TURN;
const SQUIGGLE_SEG3_LEN = SQUIGGLE_SEG1_LEN;
const MIN_WIDTH_FOR_SQUIGGLE = SQUIGGLE_SEG2_LEN;

function computeBreakLineEndPoints(x0: number, y0: number, x1: number, y1: number, outline: PolyLine2) {
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  const halfDx = x1 - mx;
  const halfDy = y1 - my;

  const halfDist = Math.sqrt(halfDx * halfDx + halfDy * halfDy);

  const h = halfDist * BREAKLINE_ANGLE_TAN;

  const dXScaledH = (h * halfDx) / halfDist;
  const dyScaledH = (h * halfDy) / halfDist;

  const dxCCW = -dyScaledH;
  const dyCCW = dXScaledH;

  const dxCW = dyScaledH;
  const dyCW = -dXScaledH;

  const rx0 = x0 + dxCCW;
  const ry0 = y0 + dyCCW;
  const rx1 = x1 + dxCW;
  const ry1 = y1 + dyCW;

  if (polyLine2IsRectangle(outline)) {
    return { rx0, rx1, ry0, ry1 };
  }

  // For PolyLine geometry, project the breakLine to the edge of the stair bounding box.
  const lineDirX = rx1 - rx0;
  const lineDirY = ry1 - ry0;
  const lineDirLen = Math.sqrt(lineDirX * lineDirX + lineDirY * lineDirY);

  if (lineDirLen < DIST_EPSILON) {
    return { rx0, rx1, ry0, ry1 };
  }

  const boundingBox = polyLine2AABB2(outline);

  const ndx = lineDirX / lineDirLen;
  const ndy = lineDirY / lineDirLen;

  const { minX, minY, maxX, maxY } = boundingBox;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const cx of [minX, maxX]) {
    for (const cy of [minY, maxY]) {
      const t = (cx - mx) * ndx + (cy - my) * ndy;
      if (t < tMin) {
        tMin = t;
      }
      if (t > tMax) {
        tMax = t;
      }
    }
  }

  return { rx0: mx + tMin * ndx, rx1: mx + tMax * ndx, ry0: my + tMin * ndy, ry1: my + tMax * ndy };
}

/**
 * Constructs a breakline oriented at 30 degrees relative to the stair intersected by the cut plane.
 * The breakline contains a small squiggle in the middle to help differentiate between stairs above
 * and below the cut.
 */
export function makeStairBreakLine(x0: number, y0: number, x1: number, y1: number, outline: PolyLine2): PolyLine2 {
  const { rx0, rx1, ry0, ry1 } = computeBreakLineEndPoints(x0, y0, x1, y1, outline);

  const dx = rx1 - rx0;
  const dy = ry1 - ry0;
  const len = Math.sqrt(dx * dx + dy * dy);

  if (len < MIN_WIDTH_FOR_SQUIGGLE) {
    return polyLine2New([rx0, ry0, rx1, ry1]);
  }

  const tx = dx / len;
  const ty = dy / len;

  const lineAngle = Math.atan2(ty, tx);

  const squiggleAdvance =
    Math.cos(SQUIGGLE_SEG1_ANGLE) * SQUIGGLE_SEG1_LEN +
    Math.cos(SQUIGGLE_SEG1_ANGLE + SQUIGGLE_SEG2_TURN) * SQUIGGLE_SEG2_LEN +
    Math.cos(SQUIGGLE_SEG1_ANGLE + SQUIGGLE_SEG2_TURN + SQUIGGLE_SEG3_TURN) * SQUIGGLE_SEG3_LEN;

  const squiggleStart = (len - squiggleAdvance) / 2;

  const coords: number[] = [rx0, ry0];

  let cx = rx0 + tx * squiggleStart;
  let cy = ry0 + ty * squiggleStart;
  coords.push(cx, cy);

  let segAngle = lineAngle + SQUIGGLE_SEG1_ANGLE;
  cx += Math.cos(segAngle) * SQUIGGLE_SEG1_LEN;
  cy += Math.sin(segAngle) * SQUIGGLE_SEG1_LEN;
  coords.push(cx, cy);

  segAngle += SQUIGGLE_SEG2_TURN;
  cx += Math.cos(segAngle) * SQUIGGLE_SEG2_LEN;
  cy += Math.sin(segAngle) * SQUIGGLE_SEG2_LEN;
  coords.push(cx, cy);

  segAngle += SQUIGGLE_SEG3_TURN;
  cx += Math.cos(segAngle) * SQUIGGLE_SEG3_LEN;
  cy += Math.sin(segAngle) * SQUIGGLE_SEG3_LEN;
  coords.push(cx, cy);

  coords.push(rx1, ry1);

  return polyLine2New(coords);
}

export function buildBreakLineBoundingTriangle(originalBreakLine: PolyLine2, tol = 0.01): PolyLine2 {
  const breakLine = polyLine2ExtendEnd(polyLine2ExtendStart(polyLine2Copy(originalBreakLine), tol), tol);

  const x0 = breakLine[0];
  const y0 = breakLine[1];
  const x1 = breakLine[breakLine.length - 2];
  const y1 = breakLine[breakLine.length - 1];

  const dx = x1 - x0;
  const dy = y1 - y0;

  const dxa = (BREAKLINE_ANGLE_COS * dx - BREAKLINE_ANGLE_SIN * dy) * BREAKLINE_ANGLE_COS;
  const dya = (BREAKLINE_ANGLE_SIN * dx + BREAKLINE_ANGLE_COS * dy) * BREAKLINE_ANGLE_COS;
  const xa = x0 + dxa;
  const ya = y0 + dya;

  const coords: number[] = [];
  for (let i = 0; i < breakLine.length; i += 2) {
    coords.push(breakLine[i], breakLine[i + 1]);
  }

  coords.push(xa, ya);
  coords.push(x0, y0);

  return polyLine2New(coords);
}

export function makeStairPlanOutlines(
  stairSystem: GeneratedStairSystem,
  floorStairCutHeight: number,
  considerNosingOverhang?: boolean,
) {
  const solidPolys: PolyLine2[] = [];
  const fillPolys: PolyLine2[] = [];
  const dashedPolys: PolyLine2[] = [];
  const dashedPolysBelowCutPlane: PolyLine2[] = [];
  const solidPolysAboveCutPlane: PolyLine2[] = [];
  const dashedPolysAboveCutPlane: PolyLine2[] = [];
  const arrowPolys: PolyLine2[] = [];
  const cutLinePolys: PolyLine2[] = [];

  for (const blockId in stairSystem.blocks) {
    const { stairPlanPolys, outline } = stairSystem.blocks[blockId];

    let lastBelowCutOutline: PolyLine2 | undefined;
    let firstAboveCutOutline: PolyLine2 | undefined;

    stairPlanPolys.forEach((stairPlanPoly, index) => {
      if (!stairPlanPoly) {
        return;
      }
      const { outline, height, outlinesPDF, overrideOutlineForPDF, nosing, edgeTypes } = stairPlanPoly;
      if (overrideOutlineForPDF) {
        if (outlinesPDF.length > 0) {
          if (height <= floorStairCutHeight) {
            solidPolys.push(...outlinesPDF);
            fillPolys.push(...outlinesPDF);
          } else {
            dashedPolys.push(...outlinesPDF);
          }
        }
      } else {
        if (height <= floorStairCutHeight) {
          lastBelowCutOutline = outline;
          if (considerNosingOverhang) {
            const { solidPolys: solidPolysNosing, dashedPolys: dashedPolysRisers } =
              splitStairPlanOutlinesConsideringNosing(outline, nosing, edgeTypes, index === 0);
            solidPolys.push(...solidPolysNosing);
            dashedPolysBelowCutPlane.push(...dashedPolysRisers);
          } else {
            solidPolys.push(outline);
          }
          fillPolys.push(outline);
        } else {
          if (!firstAboveCutOutline) {
            firstAboveCutOutline = outline;
          }
          if (considerNosingOverhang) {
            const { solidPolys: solidPolysNosing, dashedPolys: dashedPolysRisers } =
              splitStairPlanOutlinesConsideringNosing(outline, nosing, edgeTypes, index === 0);
            solidPolysAboveCutPlane.push(...solidPolysNosing);
            dashedPolysAboveCutPlane.push(...dashedPolysRisers);
          }
          dashedPolys.push(outline);
        }
      }
    });

    if (lastBelowCutOutline && firstAboveCutOutline) {
      const sharedEdge = polyLine2FindSharedEdge(lastBelowCutOutline, firstAboveCutOutline);
      if (sharedEdge) {
        cutLinePolys.push(makeStairBreakLine(sharedEdge[0], sharedEdge[1], sharedEdge[2], sharedEdge[3], outline));
      }
    }

    const arrows = makeStairUpDownArrows(stairPlanPolys);
    arrowPolys.push(...arrows);
    solidPolys.push(...arrowPolys);
  }
  return {
    solidPolys,
    fillPolys,
    dashedPolys,
    dashedPolysBelowCutPlane,
    arrowPolys,
    cutLinePolys,
    solidPolysAboveCutPlane,
    dashedPolysAboveCutPlane,
  };
}

function resolveAbsoluteVerticalDatumForTopAndBottom(
  datum: AbsoluteVerticalDatumData,
  resolvedDatums: ResolvedDatumTable,
  x: number,
  y: number,
) {
  const absoluteDatumTopAndBottom: { [id: string]: WorldInches } = {};
  const datums = [datum.topDatumId, datum.bottomDatumId];

  datums.forEach((datumId) => {
    const datum = datumId ? resolvedDatums[datumId] : undefined;
    if (datumId && datum) {
      const datumPlane = datum.plane;
      const datumHeight = getPlaneZ(datumPlane, [x, y]);
      absoluteDatumTopAndBottom[datumId] = worldInchesNew(datumHeight);
    }
  });

  return absoluteDatumTopAndBottom;
}

function resolveRelativeVerticalDatumForTopAndBottom(
  datum: RelativeVerticalDatumData,
  levelId: string /** this is the level of the stair system*/,
  levels: Building["levels"],
  queryZ: (x: number, y: number) => WorldInches,
  x: number,
  y: number,
): RelativeDatumTable {
  const relativeDatumTopAndBottom: RelativeDatumTable = {};
  const datums = [datum.topDatumId, datum.bottomDatumId];

  datums.forEach((datumId) => {
    if (datumId === undefined) {
      return;
    }
    relativeDatumTopAndBottom[datumId] = getRelativeDatumHeight(datumId, levelId, levels, queryZ, x, y);
  });

  return relativeDatumTopAndBottom;
}
